import { PlusIcon } from '@persona-id/design-tokens/icons';
import { orderBy } from 'lodash/fp';
import styled from 'styled-components';

import Permissioned from 'dashboard/components/Permissioned';
import TableControls, { FilterConfig } from 'dashboard/components-lib/TableControls';
import { Role } from 'dashboard/models/role';
import { Team } from 'dashboard/models/team';
import { User, UserStatus } from 'dashboard/models/user';
import UsersListRow from 'dashboard/views/DashboardUsersView/UsersListRow';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Table, { TableBody, TableHeaderCell, TableRow } from 'design-system/components/Table';
import { Heading } from 'design-system/components/Text';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';

const TableContainer = styled.div`
  overflow-x: scroll;
`;

interface UsersListProps {
  currentUser: User;
  filterStatuses: UserStatus[];
  filterTeamIds: string[];
  hasSamlEnabled: boolean;
  roles: Role[];
  searchTerm: string;
  teams: Team[];
  users: User[];
  changeUserRoles: (user: User, roleIds: string[]) => Promise<void>;
  openAddUserModal: () => void;
  reactivateUser: (user: User) => void;
  reinviteUser: (user: User) => Promise<void>;
  removeUser: (user: User) => void;
  rotateRecoveryCode: (userId: string) => Promise<void>;
  setFilterStatuses: (statuses: UserStatus[]) => void;
  setFilterTeamIds: (ids: string[]) => void;
  setSearchTerm: (term: string) => void;
}

const UsersList = ({
  changeUserRoles,
  currentUser,
  filterStatuses,
  filterTeamIds,
  hasSamlEnabled,
  openAddUserModal,
  reactivateUser,
  reinviteUser,
  removeUser,
  roles,
  rotateRecoveryCode,
  searchTerm,
  setFilterStatuses,
  setFilterTeamIds,
  setSearchTerm,
  users,
  teams,
}: UsersListProps) => {
  const hasTeamPermission = usePermission(Permission.TeamView);

  const userRows = orderBy(
    [
      'attributes.status',
      'attributes.first-name',
      'attributes.last-name',
      'attributes.email-address',
    ],
    ['desc', 'asc', 'asc', 'asc'],
    users,
  );

  const showAddUserButton = usePermission(Permission.UserWrite) && !hasSamlEnabled;

  const filterConfig: FilterConfig[] = [
    {
      id: 'team',
      type: 'multiselect',
      name: 'Team',
      enabled: hasTeamPermission,
      options: teams.map((team) => ({
        label: team.attributes.name,
        value: team.id,
      })),
      value: filterTeamIds,
      onChange: setFilterTeamIds,
    },
    {
      id: 'status',
      type: 'multiselect',
      name: 'Status',
      options: [
        {
          label: 'Active',
          value: UserStatus.Active,
        },
        {
          label: 'Deactivated',
          value: UserStatus.Deactivated,
        },
        {
          label: 'Pending',
          value: UserStatus.Pending,
        },
      ],
      value: filterStatuses,
      onChange: (value) => setFilterStatuses(value as UserStatus[]),
    },
  ];

  return (
    <>
      <Card>
        <Card.Body>
          <CardTitleContainer>
            <Card.Title>
              <Heading size='sm'>Users</Heading>
            </Card.Title>
            {showAddUserButton && (
              <Button
                id='users-list__show-new-user'
                data-test='users-list__show-new-user'
                onClick={openAddUserModal}
                width='auto'
                prefixIcon={<PlusIcon />}
                aria-label='Invite new user'
              >
                Invite new user
              </Button>
            )}
          </CardTitleContainer>
          <TableControls
            searchProps={{
              id: 'users-table__search-input',
              handleInput: setSearchTerm,
              value: searchTerm,
              placeholder: 'Search by name and email',
            }}
            filters={filterConfig}
          />
          <TableContainer>
            <Table>
              <TableRow>
                <StyledTableHeaderCell>Name</StyledTableHeaderCell>
                <Permissioned permission={Permission.TeamView}>
                  <StyledTableHeaderCell>Team</StyledTableHeaderCell>
                </Permissioned>
                <StyledTableHeaderCell>Email</StyledTableHeaderCell>
                <StyledTableHeaderCell>Roles</StyledTableHeaderCell>
                <Permissioned permission={Permission.UserMfaAllUsersView}>
                  <StyledTableHeaderCell>2FA</StyledTableHeaderCell>
                </Permissioned>
                <StyledTableHeaderCell>Availability</StyledTableHeaderCell>
                <StyledTableHeaderCell>{/* Context menu */}</StyledTableHeaderCell>
              </TableRow>
              <TableBody>
                {userRows.map((user) => (
                  <UsersListRow
                    user={user}
                    key={user.id}
                    roles={roles}
                    teams={teams}
                    removeUser={removeUser}
                    reinviteUser={reinviteUser}
                    reactivateUser={reactivateUser}
                    changeUserRoles={changeUserRoles}
                    rotateRecoveryCode={rotateRecoveryCode}
                    currentUser={currentUser}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card.Body>
      </Card>
    </>
  );
};

export default UsersList;

const CardTitleContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const StyledTableHeaderCell = styled(TableHeaderCell)`
  height: 44px;
`;
